import type {
  ReferenceEntry,
  ReferenceKind,
  UnresolvableReason,
  ViewSummary,
} from "@utils/viewDefinition/types";

/** The three ways a view narrows its data. Brands and markets answer "who", and are
 *  named by the card's logo strip instead. */
export type ConstraintKind = "segments" | "filters" | "questions";

export interface ConstraintGroup {
  kind: ConstraintKind;
  /** count + singular/plural noun, e.g. "1 Filter" | "3 Questions" */
  label: string;
  /** Straight from `view.counts`, so it INCLUDES references the caller cannot resolve. */
  count: number;
  /** Resolved labels for hover. Empty when enrichment is absent. */
  names: string[];
}

export type ViewConstraints =
  | { kind: "constraints"; groups: ConstraintGroup[] }
  | { kind: "unsupported" };

export type UnavailableItem = {
  kind: ReferenceKind;
  /** The reference's own id, stringified. Stable across renames, unlike displayName. */
  id: string;
  displayName: string;
  reason: UnresolvableReason;
};

export const UNAVAILABLE_REASON_COPY: Record<UnresolvableReason, string> = {
  NOT_FOUND: "no longer exists",
  INVALID_ID: "could not be read",
  DEACTIVATED: "no longer active",
  SUPERSEDED: "replaced by a newer version",
  FILTERS_NOT_ACCESSIBLE: "based on filters you can't access",
  NOT_APPLICABLE_IN_YOUR_MARKETS: "not available in your markets",
  MEMBERS_NOT_ACCESSIBLE: "its brands aren't accessible to you",
  EXPIRED: "your access to this has expired",
  NOT_ENTITLED: "not included in your subscription",
};

/**
 * Right-rail status labels: a scannable noun phrase per reason, matching the
 * vocabulary the shared-link ledger already uses ("Not licensed"). The longer
 * `UNAVAILABLE_REASON_COPY` sentences stay for tooltips and inline lists, where
 * there is room to read them.
 */
export const UNAVAILABLE_REASON_SHORT: Record<UnresolvableReason, string> = {
  NOT_FOUND: "Removed",
  INVALID_ID: "Unreadable",
  DEACTIVATED: "Inactive",
  SUPERSEDED: "Replaced",
  FILTERS_NOT_ACCESSIBLE: "Filters locked",
  NOT_APPLICABLE_IN_YOUR_MARKETS: "Not in your markets",
  MEMBERS_NOT_ACCESSIBLE: "Brands locked",
  EXPIRED: "Expired",
  NOT_ENTITLED: "Not licensed",
};

const KIND_FALLBACK_WORD: Record<ReferenceKind, string> = {
  BRAND: "brand",
  BRAND_SET: "brand set",
  MARKET: "market",
  SEGMENT: "segment",
  FILTER: "filter",
  FILTER_OPTION: "filter option",
  QUESTION: "question",
  QUESTION_OPTION: "question option",
};

function fallbackDisplayName(kind: ReferenceKind): string {
  return `a removed ${KIND_FALLBACK_WORD[kind]}`;
}

/** User-facing kind noun, singular (capitalized, for the "{Kind} · name" prefix) and
 *  plural (lowercase, for counts like "51 brands unavailable"). */
export const UNAVAILABLE_KIND_LABELS: Record<ReferenceKind, { one: string; many: string }> = {
  BRAND: { one: "Brand", many: "brands" },
  BRAND_SET: { one: "Brand set", many: "brand sets" },
  MARKET: { one: "Market", many: "markets" },
  SEGMENT: { one: "Segment", many: "segments" },
  FILTER: { one: "Filter", many: "filters" },
  FILTER_OPTION: { one: "Filter option", many: "filter options" },
  QUESTION: { one: "Question", many: "questions" },
  QUESTION_OPTION: { one: "Response", many: "responses" },
};

/**
 * Losing a filter or segment changes the population the numbers describe; losing a brand only
 * narrows the set of things being measured, and losing a market only removes a row. The capped
 * tooltip and the badge's "most severe first" reading both depend on this ordering, so a lost
 * filter can never be buried behind fifty lost brands.
 */
export const UNAVAILABLE_KIND_ORDER: ReferenceKind[] = [
  "FILTER",
  "FILTER_OPTION",
  "SEGMENT",
  "QUESTION",
  "QUESTION_OPTION",
  "BRAND_SET",
  "BRAND",
  "MARKET",
];

/** `{Kind} · displayName`, e.g. "Brand · home24" or "Filter option · Northeast Region
 *  (in Legacy Region Split)". Shared so the card's tooltip and the detail modal's
 *  uncapped list read identically. */
export function unavailableItemLabel(item: UnavailableItem): string {
  return `${UNAVAILABLE_KIND_LABELS[item.kind].one} · ${item.displayName}`;
}

/** Count-agreed kind noun, lowercase: "1 filter" / "10 brands". */
export function unavailableKindHeading(kind: ReferenceKind, count: number): string {
  const noun = UNAVAILABLE_KIND_LABELS[kind];
  return `${count} ${count === 1 ? noun.one.toLowerCase() : noun.many}`;
}

/** "51 brands unavailable" / "1 filter unavailable" when every item shares one kind;
 *  otherwise the kind-agnostic "51 unavailable". */
export function unavailableBadgeLabel(items: UnavailableItem[]): string {
  if (items.length === 0) return "0 unavailable";
  const firstKind = items[0].kind;
  const singleKind = items.every((item) => item.kind === firstKind);
  if (!singleKind) return `${items.length} unavailable`;
  return `${unavailableKindHeading(firstKind, items.length)} unavailable`;
}

const CONSTRAINT_NOUNS: Record<ConstraintKind, { one: string; many: string }> = {
  segments: { one: "Segment", many: "Segments" },
  filters: { one: "Filter", many: "Filters" },
  questions: { one: "Question", many: "Questions" },
};

const CONSTRAINT_ORDER: ConstraintKind[] = ["segments", "filters", "questions"];

/**
 * The typed counts the card footer narrows a view by. Built on `counts`, never on the
 * reference lists: counts include references this user cannot resolve, so a group can carry
 * `count: 2` with a single name. The names are the resolved entries only, which is why the
 * two can legitimately disagree.
 */
export function viewConstraints(view: ViewSummary): ViewConstraints {
  const counts = view.counts;
  if (!counts) return { kind: "unsupported" };

  const resolvedNames = labelsByKind(view);
  const namesFor = (kind: ConstraintKind): string[] =>
    resolvedNames[kind].map((entry) => entry.label).filter((label): label is string => Boolean(label));

  const groups = CONSTRAINT_ORDER.filter((kind) => counts[kind] > 0).map((kind) => {
    const count = counts[kind];
    const noun = CONSTRAINT_NOUNS[kind];
    return {
      kind,
      label: `${count} ${count === 1 ? noun.one : noun.many}`,
      count,
      names: namesFor(kind),
    };
  });

  return { kind: "constraints", groups };
}

export interface LabelGroups {
  brands: ReferenceEntry[];
  brandSets: ReferenceEntry[];
  markets: ReferenceEntry[];
  segments: ReferenceEntry[];
  filters: ReferenceEntry[];
  questions: ReferenceEntry[];
}

/** The reference resolved for this caller, so it has a name worth showing. */
const isResolved = (entry: ReferenceEntry): boolean => entry.resolved;

/**
 * Full per-kind lists of the references that RESOLVED, id preserved. Used where a count is
 * not enough: the card body's entity images and strip tooltip (the id looks up a logo or
 * colour) and the detail panel's lists. Unresolved entries are `unavailableItems`' business,
 * so a reference is never named in both places.
 */
export function labelsByKind(view: ViewSummary): LabelGroups {
  const references = view.references;
  return {
    brands: (references?.brands ?? []).filter(isResolved),
    brandSets: (references?.brandSets ?? []).filter(isResolved),
    markets: (references?.markets ?? []).filter(isResolved),
    segments: (references?.segments ?? []).filter(isResolved),
    filters: (references?.filters ?? []).filter(isResolved),
    questions: (references?.questions ?? []).filter(isResolved),
  };
}

/**
 * Every reference the caller cannot use, across all kinds, flattened out of the nested
 * `references` block and ranked by impact.
 *
 * A nested option carries its parent's name, which is where the old `parentId` pointer went:
 * "Northeast Region (in Legacy Region Split)" reads the same, sourced from the nesting instead.
 * A parent that is itself unresolved still lends its name, and an entry with no label at all
 * falls back to its typed noun rather than rendering blank.
 */
export function unavailableItems(view: ViewSummary): UnavailableItem[] {
  const references = view.references;
  if (!references) return [];

  const items: UnavailableItem[] = [];
  const push = (kind: ReferenceKind, entry: ReferenceEntry, parentName?: string): void => {
    if (entry.resolved || !entry.reason) return;
    const ownName = entry.label ?? fallbackDisplayName(kind);
    items.push({
      kind,
      id: entry.id,
      displayName: parentName ? `${ownName} (in ${parentName})` : ownName,
      reason: entry.reason,
    });
  };

  for (const entry of references.filters ?? []) {
    push("FILTER", entry);
    const parentName = entry.label ?? fallbackDisplayName("FILTER");
    for (const option of entry.options ?? []) push("FILTER_OPTION", option, parentName);
  }
  for (const entry of references.segments ?? []) push("SEGMENT", entry);
  for (const entry of references.questions ?? []) {
    push("QUESTION", entry);
    const parentName = entry.label ?? fallbackDisplayName("QUESTION");
    for (const option of entry.options ?? []) push("QUESTION_OPTION", option, parentName);
  }
  for (const entry of references.brandSets ?? []) push("BRAND_SET", entry);
  for (const entry of references.brands ?? []) push("BRAND", entry);
  for (const entry of references.markets ?? []) push("MARKET", entry);

  // Impact ordering, not backend order: a lost filter or segment changes the
  // population the numbers describe, while a lost brand only narrows the set.
  // The capped tooltip's slots must go to the former first. Stable within a kind
  // so the backend's own relative order still holds there.
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const rankDiff =
        UNAVAILABLE_KIND_ORDER.indexOf(a.item.kind) - UNAVAILABLE_KIND_ORDER.indexOf(b.item.kind);
      return rankDiff !== 0 ? rankDiff : a.index - b.index;
    })
    .map(({ item }) => item);
}

export interface UnavailableKindGroup {
  kind: ReferenceKind;
  items: UnavailableItem[];
}

/**
 * Partition (never a re-sort) of an `unavailableItems()` list into one group per kind.
 * That list already carries `UNAVAILABLE_KIND_ORDER`, so walking it in order yields the
 * groups in impact order and preserves the backend's order inside each.
 */
export function groupUnavailableByKind(items: UnavailableItem[]): UnavailableKindGroup[] {
  const groups: UnavailableKindGroup[] = [];
  for (const item of items) {
    const existing = groups.find((group) => group.kind === item.kind);
    if (existing) {
      existing.items.push(item);
    } else {
      groups.push({ kind: item.kind, items: [item] });
    }
  }
  return groups;
}
